import navStates from '@Constants/states-navigation.constant.js';
import publicStates from '@Constants/states-public.constant.js';
import AuthSvcMod from '@Services/Auth.svc.js';
import ConsultationSvcMod from '@Services/Consultation.svc.js';
import StorageSvcMod from '@Services/Storage.svc.js';
import UtilitySvcMod from '@Services/Utility.svc.js';
/** @typedef {import('./Nav.svc')} Types */
//
// States whose presentation sections render fully interactive (customer can act / consultant can edit).
const INTERACTIVE_STATES = ['view.proposal', 'view.consultation', 'preview.proposal', 'preview.consultation'];
// States rendered for print output.
const PRINT_STATES = ['print.public'];
// Everything else in the proposal family (accepted, lost, snapshot, etc.) is read-only.
//
export class Nav {
	constructor($log, NAV_STATES, StorageSvc, UtilitySvc, $q, ConsultationService, AuthSvc, PUBLIC_STATES) {
		'ngInject';
		this.$q = $q;
		this.$log = $log;
		this.AuthSvc = AuthSvc;
		this.utilityService = UtilitySvc;
		this.storageService = StorageSvc;
		this.consultationSvc = ConsultationService;
		this.NAV_STATES = NAV_STATES;
		this.PUBLIC_STATES = PUBLIC_STATES;
	}

	getNavStates() {
		return this.storageService.get('states') || this.NAV_STATES;
	}

	/** @param {string} name */
	enableNavigationToState(name) {
		const _nav_states = this.getNavStates();
		let _targetIdx;
		return _nav_states.map((x, i) => {
			x.disabled = _targetIdx !== undefined;
			if (x.name.includes(name)) _targetIdx = i;
			return x;
		});
	}

	/** @param {string} toStateName */
	isParentVisible(toStateName) {
		// * -> accepted.inspector
		if (/\.+inspector$/.test(toStateName)) {
			return false;
		}

		// * -> summary$
		if (/\.+summary$/.test(toStateName)) {
			return true;
		}

		// * -> summary$
		if (/\.+comparison$/.test(toStateName)) {
			return false;
		}

		return true;
	}

	/**
	 * @param {number} consultationId
	 * @param {string} stateCurrent
	 * @param {string} stateValid
	 * @returns {Promise<Types.NavState[]>}
	 */
	saveClientState(consultationId, stateCurrent, stateValid) {
		// reject promise if no valid param values
		if (!stateCurrent && !stateValid) {
			this.$q.reject({message: 'Invalid request Current and Valid are required'});
		}

		const currentUser = this.AuthSvc.currentUser();
		if (!this.AuthSvc.hasValidUser()) {
			this.$log.error('Current user is required');
		}

		const _errMsg = {display: 'Unable to save client state'};
		const _def = this.$q.defer();
		this.consultationSvc
			.postSaveConsultationState({
				serviceProviderId: currentUser.service_provider_id,
				userId: currentUser.id,
				consultationId: consultationId,
				// can navigate to clientState
				clientState: stateValid,
				currentClientState: stateCurrent,
			})
			.then(resObj => {
				const {data} = resObj;
				if (data?.success) {
					const _nav_states = this.enableNavigationToState(stateValid);
					this.storageService.set('states', _nav_states);
					_def.resolve(_nav_states);
				}
				if (data?.error) _def.reject(Object.assign(data.error, _errMsg));
			})
			.catch(err => _def.reject(Object.assign(err.data.error, _errMsg)));
		return _def.promise;
	}

	/** @param {string} stateName */
	isStatePublic(stateName) {
		if (!stateName) {
			this.$log.error('State name required.');
			return false;
		}
		return this.PUBLIC_STATES.includes(stateName);
	}

	/**
	 * Resolve the presentation view mode from a UI-Router state name. Replaces the per-page
	 * `viewMode` binding the v2 section components used to take: the mode is a property of the
	 * route, so it is derived here in one place rather than threaded through each page.
	 * @param {string} stateName
	 * @returns {'interactive'|'readonly'|'print'}
	 */
	getViewMode(stateName) {
		if (!stateName) {
			this.$log.error('State name required.');
			return 'readonly';
		}
		if (PRINT_STATES.includes(stateName)) return 'print';
		if (INTERACTIVE_STATES.includes(stateName)) return 'interactive';
		return 'readonly';
	}
}
//
let compMod = angular
	.module('mod.svc.NavSvc', [AuthSvcMod, ConsultationSvcMod, StorageSvcMod, UtilitySvcMod])
	.constant('PUBLIC_STATES', publicStates)
	.constant('NAV_STATES', navStates)
	.service('NavSvc', Nav);
export default (compMod = compMod.name);
